import { useDeferredValue, useMemo, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { useFilteredResources, useResourcesUniqueTags } from 'src/lib/hooks';

import { FILTER_TYPE, filterBrandBriefItems } from '../../lib';
import { Filters } from '../Filters';
import { MenuSearchField } from '../MenuSearchField';
import { SearchHint } from '../SearchHint';
import { SearchResults } from '../SearchResults';

import { useEvents } from './useEvents';
import { useStyles } from './useStyles';

const DEFAULT_FILTERS = {
  [FILTER_TYPE.types]: [],
  [FILTER_TYPE.tags]: [],
};

const propTypes = {
  className: PropTypes.string,
  resources: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  bankResources: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  onAction: PropTypes.func.isRequired,
  isApplicationModeInfinity: PropTypes.bool,
  quickSearchMode: PropTypes.bool,
  noTags: PropTypes.bool,
  shouldIncludeAudioVideoResources: PropTypes.bool,
};

const defaultProps = {
  className: '',
  isApplicationModeInfinity: false,
  quickSearchMode: false,
  noTags: false,
  shouldIncludeAudioVideoResources: false,
};

export const MenuContentAll = ({
  className,
  resources,
  bankResources,
  onAction,
  isApplicationModeInfinity,
  quickSearchMode,
  noTags,
  shouldIncludeAudioVideoResources,
}) => {
  const styles = useStyles();
  const events = useEvents({ onAction });

  const [searchValue, setSearchValue] = useState('');
  const deferredSearchValue = useDeferredValue(searchValue);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const handleSearchChange = (value) => {
    setSearchValue(value.trim());

    events.trackSearchSubmittedDebounced({
      searchTerm: value,
      tagsFilters: filters[FILTER_TYPE.tags],
      typeFilters: filters[FILTER_TYPE.types],
    });
  };

  const handleFilterChange = (filterType, newValue) => {
    setFilters((oldFilters) => {
      const newFilters = {
        ...oldFilters,
        [filterType]: newValue,
      };

      events.trackSearchSubmitted({
        searchTerm: searchValue,
        tagsFilters: newFilters[FILTER_TYPE.tags],
        typeFilters: newFilters[FILTER_TYPE.types],
      });

      return newFilters;
    });
  };

  const uniqueTags = useResourcesUniqueTags({ resources });

  const filteredResources = useFilteredResources({
    resources,
    searchText: deferredSearchValue,
    typeFilter: filters[FILTER_TYPE.types],
    tagFilter: filters[FILTER_TYPE.tags],
  });
  const filteredBankItems = useMemo(
    () => filterBrandBriefItems(deferredSearchValue, bankResources),
    [bankResources, deferredSearchValue]
  );

  const hasActiveFilters = filters[FILTER_TYPE.types].length || filters[FILTER_TYPE.tags].length;
  const shouldDisplayFilters = !quickSearchMode || deferredSearchValue;
  const shouldDisplayResults = quickSearchMode
    ? deferredSearchValue
    : deferredSearchValue || hasActiveFilters;

  return (
    <div className={cx(styles.root, className)}>
      <MenuSearchField
        className={styles.searchField}
        value={searchValue}
        onChange={handleSearchChange}
        autoFocus
      />

      {shouldDisplayFilters && (
        <Filters
          tags={uniqueTags}
          filters={filters}
          onChange={handleFilterChange}
          shouldIncludeAudioVideoResources={shouldIncludeAudioVideoResources}
        />
      )}

      {!shouldDisplayResults ? (
        <SearchHint quickSearchMode={quickSearchMode} />
      ) : (
        <SearchResults
          searchValue={deferredSearchValue}
          filters={filters}
          filteredResources={filteredResources}
          bankItems={filteredBankItems}
          onAction={onAction}
          isApplicationModeInfinity={isApplicationModeInfinity}
          quickSearchMode={quickSearchMode}
          noTags={noTags}
        />
      )}
    </div>
  );
};

MenuContentAll.displayName = 'MenuContentAll';
MenuContentAll.propTypes = propTypes;
MenuContentAll.defaultProps = defaultProps;
